package fyi.moko.hotpl.domain.usecase

import fyi.moko.hotpl.domain.model.Emotion
import fyi.moko.hotpl.domain.model.Place
import fyi.moko.hotpl.domain.model.PlaceStatus
import fyi.moko.hotpl.domain.model.RechargeCandidate
import fyi.moko.hotpl.domain.model.Visit

/**
 * Answers "지금 이 느낌이 필요한데, 어디였지?" by reading the emotion already recorded on
 * each visit ([Visit.emotion]) as a *query* axis rather than a display field — the 회복 루프
 * of `HOTPL_MASTER.md` §1.4, backlog `A-9`.
 *
 * Pure and deterministic: same places + visits + emotion → same list. No clock, no
 * platform references. The rule lives here rather than in the ViewModel so it can be
 * judged in isolation before any UI depends on it (same reasoning as
 * [RevisitWindowCalculator] and `todaysRecalls`).
 *
 * **Nothing new is persisted.** `Visit.emotion` has been stored, synced to the visits
 * subcollection and included in backups since slice 2; this selector only reads it. No
 * migration, DTO change, or Firestore rules change belongs to this feature.
 *
 * ── 다른 두 회상 규칙과 의도적으로 다른 점 ──────────────────────────────
 *  · **최근 방문을 배제하지 않는다.** [RevisitWindowCalculator]는 14일 이내 방문한
 *    장소를 제외한다 — 특별한 장소는 자주 가면 감흥이 닳기 때문이다. 회복 장소는
 *    닳지 않는다. 어제 나를 회복시킨 카페는 오늘도 회복시킨다. 두 규칙을 "통일"하려는
 *    리팩터링이 이 차이를 지우지 않도록 테스트로 고정해 뒀다.
 *  · **`liked`를 요구하지 않는다.** `todaysRecalls`는 liked 장소만 본다. 여기서는
 *    감정 기록 자체가 신호다. 하트까지 요구하면 "좋아요를 안 눌렀을 뿐 실제로 회복된"
 *    장소가 숨는다.
 *  · **`RevisitCadence`를 보지 않는다.** 카페는 대부분 ANYTIME으로 몰려 이 축이
 *    정보를 잃는다(§1.4). 시간 축은 특별한 장소 몫으로 남겨 둔다.
 */
class RechargePlaceSelector {

    /**
     * The emotions that actually appear in the user's own eligible visit history, in
     * palette ([Emotion] declaration) order — energetic → calm → reflective, so the picker
     * reads the same way every time regardless of what the data happens to contain.
     *
     * Used to build the picker: offering all eight would put dead buttons in front of a
     * user whose history only holds three. An empty result means the picker renders
     * nothing at all (no empty state — the Quiet product principle, §1.3 #5).
     */
    fun availableEmotions(places: List<Place>, visits: List<Visit>): List<Emotion> {
        val eligiblePlaceIds = eligiblePlaceIds(places)
        val present = visits
            .asSequence()
            .filter { it.placeId in eligiblePlaceIds && !it.isDeleted }
            .mapNotNull { Emotion.fromId(it.emotion) }
            .toSet()
        return Emotion.entries.filter { it in present }
    }

    /**
     * Places where [emotion] was actually recorded, strongest match first.
     *
     * Ranking, in order: matching visit count desc (more evidence wins), then the matching
     * *ratio* desc (a place that is reliably this feeling beats one that was this feeling
     * once out of many), then the most recent match, then name for a stable order.
     *
     * Count leads ratio deliberately: a single 1-of-1 visit is a perfect ratio but almost no
     * evidence, and letting it outrank a 3-of-5 place would make the list feel arbitrary.
     */
    fun select(places: List<Place>, visits: List<Visit>, emotion: Emotion): List<RechargeCandidate> {
        val eligiblePlaces = places.filter(::isEligible).associateBy { it.id }
        if (eligiblePlaces.isEmpty()) return emptyList()

        return visits
            .filter { !it.isDeleted && it.placeId in eligiblePlaces }
            .groupBy { it.placeId }
            .mapNotNull { (placeId, placeVisits) ->
                val matching = placeVisits.filter { Emotion.fromId(it.emotion) == emotion }
                if (matching.isEmpty()) return@mapNotNull null
                val place = eligiblePlaces.getValue(placeId)
                RechargeCandidate(
                    placeId = placeId,
                    placeName = place.name,
                    emotion = emotion,
                    matchingVisitCount = matching.size,
                    totalVisitCount = placeVisits.size,
                    lastMatchedAt = matching.maxOf { it.visitedAt },
                )
            }
            .sortedWith(
                compareByDescending<RechargeCandidate> { it.matchingVisitCount }
                    .thenByDescending { it.matchingVisitCount.toDouble() / it.totalVisitCount }
                    .thenByDescending { it.lastMatchedAt }
                    .thenBy { it.placeName },
            )
    }

    private fun eligiblePlaceIds(places: List<Place>): Set<String> =
        places.asSequence().filter(::isEligible).map { it.id }.toSet()

    /** Matches the `todaysRecalls` / `revisitCandidates` guard: live, actually-visited places. */
    private fun isEligible(place: Place): Boolean =
        !place.isDeleted && place.status == PlaceStatus.VISITED
}
